import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Target, MessageCircle, Database, Zap, ArrowRight, CheckCircle2,
  TrendingUp, Clock, ShieldCheck, Sparkles, Users, BarChart3, Filter, CalendarCheck, Send,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import api from "@/lib/api";
import { waLink } from "@/lib/config";
import Seo from "@/components/Seo";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/ab9a2288-03d7-4487-a34d-9cf689607406/images/e6ef4a6f9cea9b2597e66b03ea572bc1bd20bc1e1a784157853ed9e188f68ad6.png";

const STATS = [
  { value: "2.4M+", label: "Ad impressions managed" },
  { value: "5 sec", label: "Avg. lead response time" },
  { value: "38%", label: "Lower cost per lead" },
  { value: "3x", label: "More booked viewings" },
];

const SERVICES = [
  {
    icon: Target,
    title: "Meta Ads Management",
    desc: "High-intent Facebook & Instagram campaigns engineered for buyer and seller leads — optimised for qualified conversations, not vanity clicks.",
    points: ["Audience & creative strategy", "Daily optimisation", "Predictable cost per lead"],
    span: "lg:col-span-2",
  },
  {
    icon: MessageCircle,
    title: "WhatsApp Nurturing Funnels",
    desc: "Automated flows reply in 5 seconds, qualify the lead and book the viewing — 24/7.",
    points: ["Instant auto-replies", "Smart qualification", "Follow-up sequences"],
    span: "",
  },
  {
    icon: Database,
    title: "CRM Integration",
    desc: "Every lead, message and stage in one source of truth — with automated scoring & reminders.",
    points: ["Meta → CRM sync", "Lead scoring", "Pipeline automation"],
    span: "",
  },
  {
    icon: Zap,
    title: "Done-For-You Setup",
    desc: "We build the entire pixel-to-pipeline engine for your real estate business, then hand you the keys.",
    points: ["Full system install", "Team training", "Ongoing optimisation"],
    span: "lg:col-span-2",
  },
];

const STEPS = [
  { icon: Target, title: "Capture", desc: "Meta Ads drive high-intent buyers & sellers into the funnel." },
  { icon: Filter, title: "Qualify", desc: "WhatsApp bot asks 3 smart questions and scores every lead." },
  { icon: Send, title: "Nurture", desc: "Automated sequences keep leads warm until they're sales-ready." },
  { icon: CalendarCheck, title: "Close", desc: "Hot leads land on your calendar as booked viewings." },
];

const WHY = [
  { icon: Clock, title: "Speed-to-lead that wins", desc: "Leads contacted in 5 minutes convert up to 21x more. Our bots reply in 5 seconds." },
  { icon: TrendingUp, title: "Built for real estate", desc: "Not a generic agency. Every funnel, script and campaign is tuned for property." },
  { icon: ShieldCheck, title: "No wasted ad spend", desc: "We optimise for cost per qualified conversation, so you stop paying for tire-kickers." },
  { icon: Users, title: "A partner, not a vendor", desc: "We install the system, train your team and stay accountable to your pipeline." },
];

const FAQS = [
  {
    q: "How does Digitify My Biz generate real estate leads with Meta Ads?",
    a: "We build conversion-focused Facebook and Instagram campaigns around a specific offer (like a price guide or instant home valuation), then route every click straight into an automated WhatsApp conversation. This means leads are qualified while their intent is highest, and you only pay for conversations that matter.",
  },
  {
    q: "What is an automated WhatsApp nurturing funnel?",
    a: "It's an always-on system that replies to new leads within about 5 seconds, asks 3 qualifying questions (budget, timeline, area), scores the lead, and either books a viewing or starts a nurture sequence. It runs 24/7 so no lead ever goes cold while you sleep.",
  },
  {
    q: "Do I need a CRM to work with Digitify My Biz?",
    a: "No — we set it up for you. We connect your Meta lead forms and WhatsApp conversations into a CRM that becomes your single source of truth, with automated lead scoring and follow-up reminders so nothing slips through the cracks.",
  },
  {
    q: "How quickly will I see results?",
    a: "Most real estate clients see qualified conversations flowing within the first 1–2 weeks of launching campaigns. The compounding effect — a growing database of buyers and sellers you can re-market to — builds over the following months.",
  },
  {
    q: "Is this only for real estate agents?",
    a: "Our system is purpose-built for real estate professionals, brokerages and developers. That said, the same Meta Ads + WhatsApp automation + CRM engine works powerfully for any high-ticket, consultative B2B or services business.",
  },
];

function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the WhatsApp consent to continue.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "home-form" });
      toast.success("🎉 Got it! Our team will reach out shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      console.error("Lead submit failed:", err);
      toast.error(err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="lead-form" className="glass-panel rounded-3xl p-6 sm:p-8 cyan-glow">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-400">Name</label>
          <input
            data-testid="lead-name"
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className="mt-1.5 w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-400">Email</label>
          <input
            data-testid="lead-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@email.com"
            className="mt-1.5 w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors"
          />
        </div>
      </div>
      <div className="mt-4">
        <label className="text-xs uppercase tracking-wider text-slate-400">Phone / WhatsApp</label>
        <input
          data-testid="lead-phone"
          value={form.phone}
          onChange={update("phone")}
          placeholder="+91…"
          className="mt-1.5 w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors"
        />
      </div>
      <div className="mt-4">
        <label className="text-xs uppercase tracking-wider text-slate-400">What do you need help with?</label>
        <textarea
          data-testid="lead-message"
          value={form.message}
          onChange={update("message")}
          rows={3}
          placeholder="e.g. I want more buyer leads for my listings…"
          className="mt-1.5 w-full rounded-xl bg-brand-slate/60 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-cyan transition-colors resize-none"
        />
      </div>
      <label className="mt-4 flex items-start gap-3 cursor-pointer" data-testid="lead-consent-label">
        <input
          type="checkbox"
          data-testid="lead-consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-brand-cyan cursor-pointer"
        />
        <span className="text-xs text-slate-400 leading-relaxed">
          I agree to receive automated system updates and marketing case studies via WhatsApp from Digitify My Biz. Message &amp; data rates may apply.
        </span>
      </label>
      <button
        type="submit"
        disabled={loading || !consent}
        data-testid="lead-submit"
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-6 py-4 font-semibold text-white transition-all duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-60"
      >
        {loading ? "Sending…" : "Get my free strategy call"}
        <ArrowRight className="h-4 w-4" />
      </button>
      <p className="mt-3 text-center text-xs text-slate-500">No spam. We'll only reach out about your growth.</p>
    </form>
  );
}

function SectionTag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-brand-cyan/25 bg-brand-cyan/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-cyan">
      <Sparkles className="h-3.5 w-3.5" /> {children}
    </span>
  );
}

export default function Home() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid="home-page">
      <Seo schema={faqSchema} id="faq-jsonld" />

      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-24">
        <div className="absolute inset-0 hero-spotlight" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="animate-fade-up"><SectionTag>Real Estate Marketing, Automated</SectionTag></div>
            <h1 className="animate-fade-up mt-6 font-heading font-extrabold tracking-tighter text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02]" style={{ animationDelay: "80ms" }}>
              Turn Meta Ads into <span className="text-gradient">booked closings</span> — on autopilot.
            </h1>
            <p className="animate-fade-up mt-6 text-lg text-slate-300 max-w-xl leading-relaxed" style={{ animationDelay: "160ms" }}>
              We build the complete growth engine for real estate pros: high-intent Meta Ads, automated WhatsApp nurturing funnels, and CRM integration that turns clicks into qualified viewings.
            </p>
            <div className="animate-fade-up mt-8 flex flex-col sm:flex-row gap-3" style={{ animationDelay: "240ms" }}>
              <a
                href={waLink("Hi Digitify My Biz! I'd like a free strategy call for my real estate business.")}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-7 py-4 font-semibold text-white transition-all duration-300 hover:scale-[1.03] active:scale-95 cyan-glow"
              >
                Get a free strategy call <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-4 font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Explore our services
              </a>
            </div>
            <div className="animate-fade-up mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400" style={{ animationDelay: "320ms" }}>
              {["Meta Ads", "WhatsApp Funnels", "CRM Integration"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-brand-cyan" /> {t}
                </span>
              ))}
            </div>
          </div>

          <div className="animate-fade-up relative" style={{ animationDelay: "200ms" }}>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 cyan-glow">
              <img src={HERO_IMG} alt="Marketing automation funnel visualization" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-slate via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-4 glass-panel rounded-2xl px-5 py-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full grid place-items-center bg-brand-whatsapp/20 text-brand-whatsapp"><MessageCircle className="h-5 w-5" /></div>
              <div>
                <p className="text-white font-bold text-lg leading-none">5 sec</p>
                <p className="text-xs text-slate-400">avg. reply time</p>
              </div>
            </div>
            <div className="absolute -top-5 -right-2 glass-panel rounded-2xl px-5 py-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full grid place-items-center bg-brand-cyan/15 text-brand-cyan"><BarChart3 className="h-5 w-5" /></div>
              <div>
                <p className="text-white font-bold text-lg leading-none">-38%</p>
                <p className="text-xs text-slate-400">cost per lead</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-white/5 bg-brand-navy/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center lg:text-left">
              <p className="font-heading font-extrabold text-3xl sm:text-4xl text-white">{s.value}</p>
              <p className="mt-1 text-sm text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="py-24 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <SectionTag>Our Core Services</SectionTag>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
              One engine. Three weapons. Endless deals.
            </h2>
            <p className="mt-4 text-slate-400 text-lg">
              Each piece is powerful alone. Together they create an unfair advantage for your real estate business.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group glass-panel rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-brand-cyan/30 ${s.span}`}
                style={{ boxShadow: "0 0 0 0 transparent" }}
              >
                <div className="h-12 w-12 rounded-xl grid place-items-center bg-brand-cyan/10 text-brand-cyan mb-5 group-hover:scale-110 transition-transform">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="font-heading font-bold text-xl text-white">{s.title}</h3>
                <p className="mt-2 text-slate-400 leading-relaxed">{s.desc}</p>
                <ul className="mt-4 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="h-4 w-4 text-brand-cyan shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-brand-navy/30 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <SectionTag>The Pixel-to-Pipeline System</SectionTag>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
              How we turn a click into a closing
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((step, i) => (
              <div key={step.title} className="relative glass-panel rounded-2xl p-7">
                <span className="font-heading font-extrabold text-5xl text-brand-blue/30">0{i + 1}</span>
                <div className="h-11 w-11 rounded-xl grid place-items-center bg-brand-blue/15 text-brand-cyan my-4">
                  <step.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionTag>Why Choose Digitify My Biz</SectionTag>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
              We don't sell ads. We build pipelines.
            </h2>
            <p className="mt-4 text-slate-400 text-lg">
              Most agencies hand you a dashboard and disappear. We install a complete revenue system and stay accountable to one number: booked viewings.
            </p>
            <a
              href={waLink("Hi Digitify My Biz! I'd like to know how you can grow my pipeline.")}
              target="_blank"
              rel="noreferrer"
              data-testid="why-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-7 py-4 font-semibold text-white transition-all duration-300 hover:scale-[1.03] active:scale-95"
            >
              Book your free audit <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {WHY.map((w) => (
              <div key={w.title} className="glass-panel rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1">
                <div className="h-11 w-11 rounded-xl grid place-items-center bg-brand-cyan/10 text-brand-cyan mb-4">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-bold text-white">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD CAPTURE */}
      <section className="py-24 bg-brand-navy/30 border-y border-white/5">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionTag>Free Strategy Call</SectionTag>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
              Let's map your next 90 days of growth
            </h2>
            <p className="mt-4 text-slate-400 text-lg">
              Tell us where you are and we'll show you exactly how to fill your calendar with qualified viewings. No pressure, no jargon.
            </p>
            <ul className="mt-7 space-y-3">
              {["A custom Meta Ads game plan", "Your WhatsApp automation blueprint", "CRM & follow-up setup walkthrough"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-200">
                  <CheckCircle2 className="h-5 w-5 text-brand-cyan" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <LeadForm />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 scroll-mt-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-center">
            <SectionTag>Questions, Answered</SectionTag>
            <h2 className="mt-5 font-heading font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl">
              Frequently asked questions
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="glass-panel rounded-2xl border-none px-5">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading font-semibold text-white hover:no-underline text-base">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed text-base">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-3xl border border-brand-cyan/20 bg-brand-navy p-10 sm:p-16 text-center cyan-glow">
            <div className="absolute inset-0 hero-spotlight opacity-70" />
            <div className="relative">
              <h2 className="font-heading font-extrabold tracking-tight text-white text-3xl sm:text-5xl">
                Ready to fill your calendar with viewings?
              </h2>
              <p className="mt-4 text-slate-300 text-lg max-w-2xl mx-auto">
                Test our live WhatsApp bot in the corner, or talk to a human now.
              </p>
              <a
                href={waLink("Hi Digitify My Biz! I'm ready to grow my real estate business.")}
                target="_blank"
                rel="noreferrer"
                data-testid="final-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-blue hover:bg-brand-blueHover px-8 py-4 font-semibold text-white transition-all duration-300 hover:scale-[1.03] active:scale-95"
              >
                Start the conversation <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
